import React, { useEffect, useState } from "react";
import { getAuditLogs } from "../../api/crawlerApi";
import "./audit-logs.scss";

const initialFilters = { search: "", module: "", action: "", from: "", to: "" };

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("en-MY", {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(new Date(value));
};

const displayUser = (row) =>
  row.user?.name || row.user?.username || row.user?.email || (row.user_id ? `User #${row.user_id}` : "System");

const JsonBlock = ({ label, value }) => (
  <div className="audit-logs__json-block">
    <strong>{label}</strong>
    <pre>{value && Object.keys(value).length ? JSON.stringify(value, null, 2) : "No values"}</pre>
  </div>
);

export default function AuditLogsPage() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [options, setOptions] = useState({ modules: [], actions: [] });
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadRows = async (page = 1, activeFilters = filters) => {
    setLoading(true);
    try {
      const response = await getAuditLogs({ ...activeFilters, page, per_page: 25 });
      const pagination = response.data || {};
      setRows(Array.isArray(pagination.data) ? pagination.data : []);
      setMeta({
        current_page: pagination.current_page || 1,
        last_page: pagination.last_page || 1,
        total: pagination.total || 0,
      });
      setOptions(response.filters || { modules: [], actions: [] });
      setError("");
    } catch (err) {
      setError(err.message || "Failed to load audit logs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows(1, initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  const handleSubmit = (event) => {
    event.preventDefault();
    setExpandedId(null);
    loadRows(1);
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setExpandedId(null);
    loadRows(1, initialFilters);
  };

  return (
    <div className="audit-logs">
      <div className="audit-logs__heading">
        <div>
          <h2>Audit Logs</h2>
          <p>Review all recorded activity, changes, access attempts, and request details.</p>
        </div>
        <span className="audit-logs__total">{meta.total.toLocaleString()} records</span>
      </div>

      <form className="audit-logs__filters" onSubmit={handleSubmit}>
        <input value={filters.search} onChange={(e) => updateFilter("search", e.target.value)} placeholder="Search user, activity, IP or URL..." />
        <select value={filters.module} onChange={(e) => updateFilter("module", e.target.value)}>
          <option value="">All modules</option>
          {(options.modules || []).map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
        <select value={filters.action} onChange={(e) => updateFilter("action", e.target.value)}>
          <option value="">All actions</option>
          {(options.actions || []).map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
        <input type="date" title="From date" value={filters.from} onChange={(e) => updateFilter("from", e.target.value)} />
        <input type="date" title="To date" value={filters.to} onChange={(e) => updateFilter("to", e.target.value)} />
        <button type="submit">Search</button>
        <button type="button" className="audit-logs__reset" onClick={handleReset}>Reset</button>
      </form>

      {error && <div className="audit-logs__alert">{error}</div>}

      <div className="audit-logs__table-wrap">
        <table>
          <thead><tr><th>Time</th><th>User</th><th>Module</th><th>Action</th><th>Description</th><th>IP Address</th><th>Request</th><th>Details</th></tr></thead>
          <tbody>
            {!rows.length ? (
              <tr><td colSpan="8" className="audit-logs__empty">{loading ? "Loading audit logs..." : "No audit logs found."}</td></tr>
            ) : rows.map((row) => (
              <React.Fragment key={row.id}>
                <tr>
                  <td className="audit-logs__date">{formatDateTime(row.created_at)}</td>
                  <td>{displayUser(row)}</td>
                  <td><span className="audit-logs__badge">{row.module}</span></td>
                  <td><span className="audit-logs__action">{row.action}</span></td>
                  <td className="audit-logs__description">{row.description || "-"}</td>
                  <td>{row.ip_address || "-"}</td>
                  <td><strong>{row.method || "-"}</strong><small>{row.url || "-"}</small></td>
                  <td><button className="audit-logs__details-button" type="button" onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}>{expandedId === row.id ? "Hide" : "View"}</button></td>
                </tr>
                {expandedId === row.id && (
                  <tr className="audit-logs__detail-row"><td colSpan="8">
                    <div className="audit-logs__detail-grid">
                      <JsonBlock label="Old values" value={row.old_values} />
                      <JsonBlock label="New values" value={row.new_values} />
                    </div>
                    <div className="audit-logs__agent"><strong>User agent:</strong> {row.user_agent || "-"} <strong>Reference:</strong> {row.reference_id || "-"}</div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="audit-logs__pagination">
        <button type="button" disabled={loading || meta.current_page <= 1} onClick={() => loadRows(meta.current_page - 1)}>Previous</button>
        <span>Page {meta.current_page} of {meta.last_page}</span>
        <button type="button" disabled={loading || meta.current_page >= meta.last_page} onClick={() => loadRows(meta.current_page + 1)}>Next</button>
      </div>
    </div>
  );
}
